/**
 * <collection-carousel>
 *
 * A row of collection cards: a Swiper below 1024px, a four-across CSS grid above it.
 *
 * Loaded from sections/section-featured-collections.liquid together with
 * assets/swiper-bundle.min.js. Both are `defer`, so the Swiper global is guaranteed to
 * exist by the time this file runs — the guard below is only for the case where the
 * vendor file failed to load at all.
 *
 * This is assets/product-carousel.js plus one behaviour: it destroys the Swiper above a
 * breakpoint and rebuilds it below. The Swiper config is otherwise identical, on
 * purpose — see the note in the section about matching the proven row exactly.
 *
 * WHAT THIS COMPONENT DOES NOT DO, and must not start doing again: it does not add or
 * remove the `swiper` / `swiper-wrapper` / `swiper-slide` class names. An earlier build
 * did, so that the desktop grid would never have swiper.min.css applied to it. The idea
 * was sound and the result was not — the first card ended up flush against the edge of
 * the screen instead of on the section inset. The grid now out-specifies swiper.min.css
 * from the section's own style block, which can be verified by reading it rather than
 * by loading a page.
 *
 * Expected markup — the same shape product-carousel expects:
 *
 *   <collection-carousel data-desktop-breakpoint="1024" data-gap-mobile="20" …>
 *     <div class="swiper">
 *       <div class="swiper-wrapper">
 *         <div class="swiper-slide"> … card … </div>
 *
 * Slide WIDTHS come from CSS custom properties in the section, so the maths lives next
 * to the markup it describes; `slidesPerView: 'auto'` just tells Swiper to measure them.
 *
 * The gap and the left inset cannot work that way, and both arrive as data attributes
 * so the section stays the single source of truth:
 *
 * - `spaceBetween` — Swiper computes slide positions from its own params, so a gap
 *   applied only as a CSS margin would leave its translate maths short.
 * - `slidesOffsetBefore` / `slidesOffsetAfter` — the page inset at each end of the
 *   track. Before: the first card starts level with the section title but can still
 *   travel PAST that inset and fully off the left edge when dragged. After: the last
 *   card stops short of the right edge instead of running flush against it. Padding on
 *   `.swiper` does not work for either — Swiper's stylesheet is unlayered and sets
 *   `.swiper { padding: 0 }`, which outranks any Tailwind utility regardless of
 *   specificity.
 */

if (!customElements.get('collection-carousel')) {
  customElements.define(
    'collection-carousel',
    class CollectionCarousel extends HTMLElement {
      /* Where the carousel becomes a four-across grid. Higher than the theme's usual
         `md` (768px) because four frames at 768px would be ~150px wide each. The
         section's style block switches to the grid at the same 64rem — if it moves, it
         moves in both places. */
      static DESKTOP_BREAKPOINT = 1024;

      /* The tablet step inside Swiper's own breakpoints, matching the theme's `md`. */
      static TABLET_BREAKPOINT = 768;

      connectedCallback() {
        this.container = this.querySelector('.swiper');

        if (!this.container || typeof Swiper === 'undefined') {
          return;
        }

        this.handleFocusIn = this.handleFocusIn.bind(this);
        this.handleBreakpoint = this.handleBreakpoint.bind(this);

        const breakpoint = this.number('desktopBreakpoint', CollectionCarousel.DESKTOP_BREAKPOINT);
        this.desktopQuery = window.matchMedia(`(min-width: ${breakpoint}px)`);
        this.desktopQuery.addEventListener('change', this.handleBreakpoint);

        this.handleBreakpoint();
      }

      disconnectedCallback() {
        if (this.desktopQuery) {
          this.desktopQuery.removeEventListener('change', this.handleBreakpoint);
        }
        this.destroySwiper();
      }

      /* A data attribute that must parse as a number, or the fallback. */
      number(name, fallback) {
        const value = parseFloat(this.dataset[name]);
        return Number.isFinite(value) ? value : fallback;
      }

      handleBreakpoint() {
        if (this.desktopQuery.matches) {
          this.destroySwiper();
        } else {
          this.buildSwiper();
        }
      }

      buildSwiper() {
        if (this.swiper) return;

        /* Defaults match --spacing-page / --spacing-page-lg, so a carousel that somehow
           renders without its data attributes still lines up with the sitewide inset
           rather than sitting flush to the viewport edge. */
        const gapMobile = this.number('gapMobile', 20);
        const gapTablet = this.number('gapTablet', 30);
        const offsetMobile = this.number('offsetMobile', 15);
        const offsetTablet = this.number('offsetTablet', 50);

        this.swiper = new Swiper(this.container, {
          /* Widths come from CSS. See the note at the top of this file. */
          slidesPerView: 'auto',

          /* Free mode gives the drag its momentum, so the row settles naturally instead
             of snapping to a slide. `sticky: false` is the point — with snapping on, a
             card could never come to rest half off-screen, which is the whole look. */
          freeMode: { enabled: true, sticky: false, momentumBounce: false },

          grabCursor: true,

          /* forceToAxis so a two-finger trackpad gesture only drives the carousel when
             it is genuinely horizontal — otherwise a vertical scroll past the section
             would hijack the page. */
          mousewheel: { forceToAxis: true },

          a11y: { enabled: true },

          /* Arrow-key control. The focus handler further down only helps when a slide
             CONTAINS something focusable — true of the collection cards, but not of the
             social section, whose slides are plain images. Without this, that section's
             row would be a scrollable region with no keyboard operation at all.

             `onlyInViewport` is what keeps it polite: the keys are only claimed while
             the carousel is actually on screen, and Swiper only takes left/right for a
             horizontal track, so up/down still scroll the page.

             `pageUpDown` is turned OFF deliberately — it defaults to true in Swiper and
             would claim Page Up / Page Down as well, which are how a keyboard user
             scrolls the PAGE. Arrow keys are enough to drive the row. Verified against
             the vendored bundle, which also self-disables the listener on destroy, so
             the desktop breakpoint releases the keys with it. */
          keyboard: { enabled: true, onlyInViewport: true, pageUpDown: false },

          /* The inset is applied at BOTH ends of the track, so the first card starts
             level with the section title and the last one comes to rest level with the
             section's right edge rather than flush against the screen. */
          spaceBetween: gapMobile,
          slidesOffsetBefore: offsetMobile,
          slidesOffsetAfter: offsetMobile,
          breakpoints: {
            [CollectionCarousel.TABLET_BREAKPOINT]: {
              spaceBetween: gapTablet,
              slidesOffsetBefore: offsetTablet,
              slidesOffsetAfter: offsetTablet,
            },
          },
        });

        /* Every card is a link, so tabbing walks into slides that are scrolled
           off-screen. Swiper does not follow focus on its own, which would leave a
           keyboard user with an invisible focus ring — a WCAG 2.1 AA failure.
           `focusin` rather than `focus` because focus does not bubble. */
        this.addEventListener('focusin', this.handleFocusIn);
      }

      destroySwiper() {
        if (!this.swiper) return;

        this.removeEventListener('focusin', this.handleFocusIn);

        /* destroy(deleteInstance, cleanStyles) — cleanStyles strips the inline
           transform and widths Swiper wrote, which would otherwise sit on the wrapper
           and fight the desktop grid. The CLASS NAMES stay; the section's style block
           overrides them by specificity. */
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      handleFocusIn(event) {
        if (!this.swiper) return;

        const slide = event.target.closest('.swiper-slide');
        if (!slide) return;

        const index = this.swiper.slides.indexOf(slide);
        if (index === -1) return;

        this.swiper.slideTo(index);
      }
    },
  );
}
